<template>
  <transition name="fade">
    <div id="app" class="app" v-if="ready"  :class="{'dark-mode': darkMode}">
      <navbar/>
      <error class="absolute"></error>
      <success/>
      <action-check/>

      <div class="app-main">
        <router-view class="view main"></router-view>
      </div>

      <div class="app-sidebar">
        <router-view class="view numpad" name="numpad" :disabled="!pinEnabled"></router-view>
      </div>
    </div>
  </transition>
</template>

<script>
import moment from 'moment';
import {writeTrackLog} from "../utils/tracking";
import {syncLocalToVue} from './../utils/sync'
import {isOnline} from "../utils/online";

import Navbar from "../components/layout/Navbar";
import Error from "../components/app/Error";
import Success from "../components/app/Success";
import ActionCheck from "../components/app/ActionCheck";
import {setLanguage} from "../translations/main";

export default {
  name: 'application',

  components: {ActionCheck, Success, Error, Navbar},

  data() {
    return {
      ready: false,
      pinEnabled: true,
      preRegistering: false
    }
  },

  computed: {
    deviceReady() {
      return this.$store.state.currentDevice !== null && this.$store.state.currentDevice !== undefined
    },

    authorized() {
      return this.$store.state.token !== null
    },

    currentDevice() {
      return this.$store.state.currentDevice
    },

    currentLocation() {
      return this.$store.getters.currentLocation
    },

    initialized() {
      return this.authorized && this.currentDevice !== null && this.currentLocation !== null && this.employees !== null
    },

    locations() {
      return this.$store.state.locations
    },

    employees() {
      return this.$store.getters.employees
    },

    workday() {
      return this.$store.getters.workdays
    },

    currentEmployee() {
      return this.$store.getters.currentEmployee
    },

    currentActivity() {
      return this.$store.getters.currentActivityForEmployee
    },

    darkMode() {
      return this.$store.state.darkMode
    }
  },

  methods: {
    init() {
      if (this.currentDevice === null || this.currentDevice === undefined) return this.$router.push({name: 'login'});

      const self = this;
      syncLocalToVue(self);
    },

    logout(needsConfirmation = true) {
      if (needsConfirmation) {
        if (!confirm(this.$t('messages.logout'))) return;
      }

      if (this.$store.state.enableTracking) writeTrackLog("LOGOUT");
      this.$store.commit('clearTimesheetEntries');
      this.$store.commit('clearWorkdays');
      this.$store.commit('clearRows');
      this.$store.commit('clearPackagings');
      this.$store.commit('clearTeams');
      this.$store.commit('clearActivities');
      this.$store.commit('clearCurrentEmployee');
      this.$store.commit('clearEmployees');
      this.$store.commit('clearCurrentLocation');
      this.$store.commit('clearLocations');
      this.$store.dispatch('clearErrors');
      this.$store.commit('clearCurrentDevice');
      this.$store.commit('clearToken');

      setLanguage('en');

      if (this.$route.name !== 'login') return this.$router.push({name: 'login'});
    },

    determineRoute(timesheetEntry) {
      const status = this.getTimesheetEntryStatus(timesheetEntry);
      const activity = this.$store.getters.activityById(timesheetEntry.activity_id);

      if (process.env.NODE_ENV === 'development') console.log('determine route', timesheetEntry, status)

      const route = (status === 'new' && this.preRegistering)
          ? this.determineRouteForNewEntry(activity)
          : this.determineRouteForExistingEntry(timesheetEntry, status, activity);

      if (this.$store.state.enableTracking) writeTrackLog('Next route: ' + JSON.stringify(route));

      return route;
    },

    determineRouteForNewEntry(activity) {
      this.preRegistering = true;

      if (this.currentDevice.pre_register_activities) {
        if (activity.register_row_numbers && this.currentActivity.row_id === null) return {name: 'row'};
      }

      if (process.env.NODE_ENV === 'development') console.log('no longer preregistering');

      this.preRegistering = false;
      this.$store.commit('setChecked');
    },

    determineRouteForExistingEntry(timesheetEntry, status, activity) {
      if (process.env.NODE_ENV === 'development') console.log('routeFor existing', timesheetEntry, status);

      if (timesheetEntry.on_hold) {
        this.$store.dispatch('updateActivity', {
          id: this.currentActivity.id,
          on_hold: false
        });

        return this.$store.commit('setChecked');
      }

      if (this.currentDevice.pre_register_activities) {
        if (activity.register_team_numbers && (!timesheetEntry.team_name || timesheetEntry.team_name === '')) return {name: 'team'};
        if (activity.register_package_types && !timesheetEntry.packaging) return {name: 'packaging'};
        if (activity.register_amounts && timesheetEntry.amount === null) return {name: 'amount'};
        if (activity.register_row_numbers && (!timesheetEntry.row_id || timesheetEntry.row_id === '')) return {name: 'row'};
        if (activity.register_row_status && !timesheetEntry.row_status) return {name: 'row-status'};
        if (activity.register_row_percentage && !timesheetEntry.row_percentage) return {name: 'row-percentage'};
      } else {
        if (activity.register_team_numbers && (!timesheetEntry.team_name || timesheetEntry.team_name === '')) return {name: 'team'};
        if (activity.register_package_types && !timesheetEntry.packaging) return {name: 'packaging'};
        if (activity.register_amounts && timesheetEntry.amount === null) return {name: 'amount'};
        if (activity.register_row_numbers && !timesheetEntry.row_id) return {name: 'row'};
        if (activity.register_row_status && !timesheetEntry.row_status) return {name: 'row-status'};
        if (activity.register_row_percentage && !timesheetEntry.row_percentage) return {name: 'row-percentage'};

      }

      if (status === 'completed' && this.preRegistering) {
        if (process.env.NODE_ENV === 'development') console.log('PreRegister::Status Completed');
        this.$store.dispatch('updateActivity', timesheetEntry);

        return this.$store.commit('setChecked');
      } else if (status === 'completed' && !this.preRegistering) {
        if (process.env.NODE_ENV === 'development') console.log('No PreRegister::Status Completed');
        if (!timesheetEntry.end) {
          this.$store.commit('syncOnline', true);
          this.$store.dispatch('updateActivity', {
            id: timesheetEntry.id,
            end: new moment().toISOString()
          });
        }

        return this.currentDevice.pre_register_activities && this.$route.name !== 'activity'
          ? this.$store.commit('setChecked', {name: 'pin'})
          : this.$store.commit('setChecked');
      }

      return {name: 'activity'}
    },

    getTimesheetEntryStatus(timesheetEntry) {
      if (!timesheetEntry || !timesheetEntry.activity_id) return 'completed';

      const activity = this.$store.getters.activityById(timesheetEntry.activity_id);

      if (
          !activity.register_team_numbers
          && !activity.register_package_types
          && !activity.register_amounts
          && !activity.register_row_numbers
          && !activity.register_row_status
          && !activity.register_row_percentage
      ) return 'completed';

      if (
          (timesheetEntry.team_name === null || timesheetEntry.team_name === '' || timesheetEntry.team_name === undefined)
          && timesheetEntry.packaging === null
          && timesheetEntry.amount === null
          && timesheetEntry.row_id === null
          && timesheetEntry.row_status === null
          && timesheetEntry.row_percentage === null
      ) {
        return 'new';
      }

      if (activity.register_team_numbers && (timesheetEntry.team_name === null || timesheetEntry.team_name === '')) {
        return 'team';
      }

      if (activity.register_packaging && timesheetEntry.packaging === null) return 'packaging';

      if (this.currentDevice.pre_register_activities) {
        if (activity.register_row_numbers && timesheetEntry.row_id === null) return 'row';
        if (activity.register_row_status && timesheetEntry.row_status === null) return 'row_status';
        if (activity.register_row_percentage && timesheetEntry.row_percentage === null) return 'row_percentage';
        if (activity.register_amounts && timesheetEntry.amount === null) return 'amount';

        return 'completed'
      }

      if (activity.register_amounts && timesheetEntry.amount === null) return 'amount';
      if (activity.register_row_numbers && timesheetEntry.row_id === null) return 'row';
      if (activity.register_row_status && timesheetEntry.row_status === null) return 'row_status';
      if (activity.register_row_percentage && timesheetEntry.row_percentage === null) return 'row_percentage';

      return 'completed'
    }
  },

  watch: {
    deviceReady: function (val) {
      if (!val) {
        return;
      }
      let online = isOnline()
      if (online) this.$store.dispatch('authenticateDevice', this.currentDevice).then(() => this.ready = true);
      if (!online) this.init();
    },

    initialized: function (val) {
      if (val && this.$route.name !== 'pin') return this.$router.push({name: 'pin'})
    },
  },

  created() {
    if (process.env.NODE_ENV === 'development') console.log('Application:created', {ready: this.deviceReady});
    if (this.deviceReady) {
      if (process.env.NODE_ENV === 'development') console.log('Application:created::isOnline()')
      let online = isOnline();
      if (online) this.$store.dispatch('authenticateDevice', this.currentDevice).then(() => this.ready = true);
      else if (!online) this.init();
    }
  },

  mounted() {
    if (this.$route.name === 'login' || this.$route.name === 'choose-location') this.ready = true;

    window.events.$on('disableNumpad', () => {
      this.pinEnabled = false
    });

    window.events.$on('enableNumpad', () => {
      this.pinEnabled = true
    });

    window.events.$on('authenticateLocalDevice', () => {
      this.init();
    });
  }
}
</script>
